/**
 * Shared formatting helpers for job/pipeline run data — used by JobList,
 * JobProgressBar, and PipelineRunsTab so the "time ago" / duration
 * formatting stays identical everywhere a run is shown.
 */

/** Coarse relative-time label for a run timestamp (e.g. "5m ago", "2d ago"). */
export function formatRelativeTime(iso: string): string {
  const diffSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return "just now";
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.round(diffHr / 24)}d ago`;
}

export function formatDuration(seconds: number | null): string | null {
  if (seconds === null) return null;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
}

/** How one run status looks wherever a run is shown (Run tab chip, job
 *  progress bar, Pipeline Runs table): one map, so a new status is styled once. */
export interface RunStatusStyle {
  label: string;
  /** Chip background, text and border classes. */
  chip: string;
  text: string;
  /** Progress-bar fill. */
  bar: string;
}

const RUN_STATUS_STYLES: Record<string, RunStatusStyle> = {
  queued: {
    label: "Queued",
    chip: "bg-white/10 text-white/50 border-white/20",
    text: "text-white/50",
    bar: "bg-white/30",
  },
  running: {
    label: "Running",
    chip: "bg-blue-500/10 text-blue-400 border-blue-400/20",
    text: "text-blue-400",
    bar: "bg-blue-500",
  },
  completed: {
    label: "Completed",
    chip: "bg-emerald-500/10 text-emerald-400 border-emerald-400/20",
    text: "text-emerald-400",
    bar: "bg-emerald-500",
  },
  failed: {
    label: "Failed",
    chip: "bg-red-500/10 text-red-400 border-red-400/20",
    text: "text-red-400",
    bar: "bg-red-500",
  },
  cancelled: {
    label: "Cancelled",
    chip: "bg-amber-500/10 text-amber-300 border-amber-400/20",
    text: "text-amber-300",
    bar: "bg-amber-400",
  },
};

/** The style for a run status; an unknown backend value shows as itself, in grey. */
export function runStatusStyle(status: string): RunStatusStyle {
  return (
    RUN_STATUS_STYLES[status] ?? {
      label: status,
      chip: "bg-white/10 text-white/40 border-white/20",
      text: "text-white/40",
      bar: "bg-white/20",
    }
  );
}
